/**
 * 객체 설정 > 웹 카테고리 객체 Store
 *
 * @since 2019-12-09
 * @author jungmin87.kim
 */

import {ObjectSecProfileApiPrefix as objApiPrefix} from 'utils'
import {isPreDefine} from 'objectSettingsUtils'

// 웹 카테고리 객체 데이터
const initWebObjectData = () => {
  // TAMS
  // * name -> web_cate_obj_name
  // * desc -> web_cate_obj_desc
  return {
    web_cate_obj_id: void 0,
    upper_web_cate_obj_id: void 0,
    web_cate_obj_name: '',
    web_cate_obj_desc: '',
    example_site: void 0,
    cfg_adm: void 0,
    cfg_dt: void 0
  }
}

// URL 데이터
const initUrlData = () => {
  // TAMS
  // * url -> web_cate_url_url
  // * desc -> web_cate_url_desc
  // * chk_type -> web_cate_url_chk_type
  return {
    web_cate_url_id: void 0,
    web_cate_url_url: '',
    web_cate_url_desc: '',
    web_cate_url_chk_type: 1,
    cfg_adm: void 0,
    cfg_dt: void 0
  }
}

export const state = () => ({
  webObjectData: initWebObjectData(),
  webObjectDataList: [],
  urlData: initUrlData(),
  urlDataList: [],
  urlTotalCount: 0,
  selectedData: void 0, // Web Category 선택된 데이터
  selectedUrlData: void 0, // Url 선택된 데이터
  copyData: void 0,
  searchCase: false,
  searchText: '',
  // TAMS
  // * url -> web_cate_url_url
  // * desc -> web_cate_url_desc
  searchType: ['all', 'web_cate_url_url', 'web_cate_url_desc']
})

export const mutations = {
  // 웹 카테고리 목록 초기화
  resetWebObjectDataList (state) {
    state.webObjectDataList = []
  },
  // 웹 카테고리 목록 설정
  setWebObjectDataList (state, data) {
    state.webObjectDataList = Object.freeze(data)
  },
  // 웹 카테고리 객체 초기화
  resetWebObjectData (state) {
    state.webObjectData = initWebObjectData()
  },
  // 웹 카테고리 객체 설정
  setWebObjectData (state, data) {
    state.webObjectData = data
  },
  // 웹 카테고리 객체 추가
  addWebObjectData (state, data) {
    const tempDataList = _.cloneDeep(state.webObjectDataList)
    tempDataList.unshift(data)
    state.webObjectDataList = Object.freeze(tempDataList)
  },
  // 웹 카테고리 객체 수정
  updateWebObjectData (state, data) {
    const tempDataList = _.cloneDeep(state.webObjectDataList)
    const index = _.findIndex(tempDataList, {web_cate_obj_id: data.web_cate_obj_id})
    if (index === -1) return
    tempDataList[index] = data
    state.webObjectDataList = Object.freeze(tempDataList)
  },
  // 웹 카테고리 객체 삭제
  deleteWebObjectData (state, data) {
    const tempDataList = _.cloneDeep(state.webObjectDataList)
    const itemIndex = _.findIndex(tempDataList, {web_cate_obj_id: data})
    if (itemIndex === -1) return
    tempDataList.splice(itemIndex, 1)
    state.webObjectDataList = Object.freeze(tempDataList)
  },
  // 웹 카테고리 객체 일괄 삭제
  deleteBatchWebObjectData (state, data) {
    const tempDataList = _.cloneDeep(state.webObjectDataList)

    _.forEach(data, (o) => {
      const itemIndex = _.findIndex(tempDataList, {web_cate_obj_id: o.web_cate_obj_id})
      if (itemIndex === -1) return
      tempDataList.splice(itemIndex, 1)
    })
    state.webObjectDataList = Object.freeze(tempDataList)
  },
  // 선택된 데이터 초기화
  resetObjectSelectData (state) {
    state.selectedData = void 0
  },
  // 웹 카테고리 객체 선택
  selectedWebCategoryObject (state, data) {
    state.selectedData = _.cloneDeep(data)
  },
  resetWebCopyData (state) {
    state.copyData = void 0
  },
  // 복사된 데이터
  copyWebObjectData (state) {
    const selectedData = _.cloneDeep(state.selectedData)
    // TAMS (cfg_adm, admin_login_uname)
    const ignoreList = ['admin_login_uname', 'cfg_adm_id', 'cfg_dt', 'cfg_layer', 'itemIndex', 'web_cate_obj_id', 'ref']
    const copyData = _.omit(selectedData, ignoreList)
    state.copyData = copyData
  },
  pasteWebObjectData (state) {
    if (!state.copyData) return
    state.webObjectData = _.assign(_.cloneDeep(state.copyData), {
      'web_cate_obj_predef': 0 // TAMS (predef -> web_cate_obj_predef)
    })
  },
  // ======================= URL
  // Url 항목 초기화
  resetUrlDataList (state) {
    state.urlTotalCount = 0
    state.urlDataList = []
  },
  // Url 항목 설정
  setUrlDataList (state, data) {
    state.urlTotalCount = data.count
    state.urlDataList = data.url_list
  },
  setUrlData (state, data) {
    state.urlData = data
  },
  setSelectedUrlData (state, data) {
    state.selectedUrlData = _.cloneDeep(data)
  },
  // Url 데이터 초기화
  resetUrlData (state) {
    state.urlData = initUrlData()
  },
  setSearch (state, {searchCase, searchText, searchType} = {}) {
    state.searchCase = searchCase
    state.searchText = searchText
    state.searchType = searchType
  },
  resetSearch (state) {
    state.searchCase = false
    state.searchText = ''
    // TAMS
    // * url -> web_cate_url_url
    // * desc -> web_cate_url_desc
    state.searchType = ['all', 'web_cate_url_url', 'web_cate_url_desc']
  }
}

export const actions = {
  // 웹 카테고리 항목 조회
  async reqGetWebObjectDataList ({commit, dispatch, state}, {loadingShow = true, loadingHide = true, isAll = false} = {}) {
    let url = ''
    if (isAll) {
      url = `${objApiPrefix}/webcategory/objects`
    } else {
      url = `${objApiPrefix}/webcategory/objects`
    }
    const {code, result} = await this.$axios.$get(url, {loadingShow, loadingHide})
    if (code === 'ok') {
      commit('setWebObjectDataList', result)
      commit('resetObjectSelectData', void 0)
      commit('setSelectedUrlData', void 0)
      commit('resetSearch')
    }
  },
  // 웹 카테고리 객체 조희
  async reqGetWebObjectData ({commit, dispatch, state}, {loadingShow = true, loadingHide = true} = {}) {
    const objectId = _.get(state.selectedData, ['web_cate_obj_id'])
    const url = `${objApiPrefix}/webcategory/objects/${objectId}`
    const {code, result} = await this.$axios.$get(url, {loadingShow, loadingHide})

    if (code === 'ok') {
      commit('setWebObjectData', result)
    }
  },
  // 웹 카테고리 객체 추가
  async reqAddWebObjectData ({commit, dispatch, state}, {loadingShow = true, loadingHide = true, successAlert = true} = {}) {
    const webObjectData = state.webObjectData
    const url = `${objApiPrefix}/webcategory/objects`
    // TAMS (res)
    const res = await this.$axios.$post(url, webObjectData, { loadingShow, loadingHide, successAlert })

    if (res.code === 'ok') {
      commit('addWebObjectData', res.result)
      dispatch('common/common/setIsChanged', true, {root: true})
    }
    return res
  },
  // 웹 카테고리 객체 수정
  async reqUpdateWebObjectData ({commit, state, dispatch, rootState}, {loadingShow = true, loadingHide = true, successAlert = true} = {}) {
    const data = state.webObjectData
    const objectId = data.web_cate_obj_id

    const url = `${objApiPrefix}/webcategory/objects/${objectId}`
    // TAMS (res)
    const res = await this.$axios.$put(url, data, { loadingShow, loadingHide, successAlert })
    if (res.code === 'ok') {
      commit('updateWebObjectData', res.result)
      dispatch('common/common/setIsChanged', true, {root: true})
    }
    return res
  },
  // 웹 카테고리 객체 삭제
  // TAMS
  async reqDeleteWebObjectData ({commit, dispatch, state}, {loadingShow = true, loadingHide = true, successAlert = true} = {}) {
    const objectId = state.selectedData.web_cate_obj_id
    if (isPreDefine(state.selectedData)) {
      return {isPreDefine: true}
    }

    // TAMS
    const url = `${objApiPrefix}/webcategory/objects/${objectId}`
    const res = await this.$axios.$delete(url, { isReturnError: true, loadingShow, loadingHide, successAlert })

    if (res.code === 'ok') {
      commit('deleteWebObjectData', objectId)
      commit('resetObjectSelectData')

      dispatch('common/common/setIsChanged', true, {root: true})
      commit('resetObjectSelectData')
      return true
    } else if (res.code === '32010123') { // TAMS
      const selectedData = _.cloneDeep(state.selectedData)
      return {
        code: 'ok',
        result: {
          can_not_delete: [_.assign(selectedData, {cause: res.result.msg})],
          force_delete_pass: []
        }
      }
    } else {
      return res
    }
  },
  // 웹 카테고리 객체 벌크 삭제
  async reqBulkDeleteWebObjectData ({commit, dispatch, state}, {loadingShow = true, loadingHide = true, successAlert = true} = {}) {
    const params = {
      // TAMS (name -> web_cate_obj_name)
      items: _.map(state.selectedData, o => _.pick(o, ['web_cate_obj_id', 'web_cate_obj_name', 'force']))
    }

    if (isPreDefine(state.selectedData)) {
      return {isPreDefine: true}
    }

    const url = `${objApiPrefix}/webcategory/objects`
    const {code, result} = await this.$axios.$delete(url, {data: params, loadingShow, loadingHide, successAlert})

    const selectedData = _.cloneDeep(state.selectedData)
    const res = {
      code,
      // TAMS (result)
      result: {
        can_not_delete: _.get(result, ['can_not_delete'], []).map(o => _.assign(o, _.find(selectedData, ['web_cate_obj_id', o['web_cate_obj_id']]))),
        force_delete_pass: _.get(result, ['force_delete_pass'], []).map(o => _.assign(o, _.find(selectedData, ['web_cate_obj_id', o['web_cate_obj_id']]))),
        errors: _.get(result, ['errors'], []).map(o => _.assign(o, _.find(selectedData, ['web_cate_obj_id', o['web_cate_obj_id']])))
      }
    }
    if (!(code === 'ok' || code === 'nok' || code === 'pok')) return

    // TAMS
    res.code = 'ok'
    if (res.result.errors.length > 0 ||
      res.result.can_not_delete.length > 0 ||
      res.result.force_delete_pass.length > 0) {
      const errors = _.unionBy(res.result.can_not_delete, res.result.force_delete_pass, res.result.errors, 'web_cate_obj_id')
      for (let i = 0; i < params.items.length; i++) {
        const item = params.items[i]
        let errorIndex = false
        errorIndex = _.findIndex(errors, {web_cate_obj_id: item.web_cate_obj_id})
        if (errorIndex === -1) {
          commit('deleteWebObjectData', item.web_cate_obj_id)
        }
      }
    } else {
      commit('deleteBatchWebObjectData', params.items)
    }
    if (code === 'ok' || code === 'pok') {
      dispatch('common/common/setIsChanged', true, {root: true})
    }
    commit('resetObjectSelectData')
    return res
  },
  // ======================= URL
  // url 목록 조회
  async reqGetUrlDataList ({commit, state}, {loadingShow = true, loadingHide = true, param}) {
    const objectId = _.get(state.selectedData, 'web_cate_obj_id', void 0)
    if (!objectId) return false
    const url = `${objApiPrefix}/webcategory/objects/${objectId}/urls`
    try {
      const {code, result} = await this.$axios.$get(url, {params: param, loadingShow, loadingHide})
      if (code === 'ok') {
        commit('setUrlDataList', result)
        return true
      }
    } catch (e) {}
    return false
  },
  async reqGetUrlData ({commit, state}) {
    const objectId = _.get(state.selectedData, 'web_cate_obj_id', void 0)
    const urlId = _.get(state.selectedUrlData, [0, 'web_cate_url_id'], void 0)
    if (!urlId || !objectId) return false
    const url = `${objApiPrefix}/webcategory/objects/${objectId}/urls/${urlId}`
    const {code, result} = await this.$axios.$get(url)
    if (code === 'ok') {
      commit('setUrlData', result)
    }
  },
  // url 삭제
  async reqDelUrlDataList ({commit, state, dispatch}, {param}) {
    const res = { code: 'nok', errors: [] }
    const objectId = _.get(state.selectedData, 'web_cate_obj_id', void 0)
    if (param.items.length === 1) {
      const urlId = _.get(param.items, [0, 'web_cate_url_id'])
      const url = `${objApiPrefix}/webcategory/objects/${objectId}/urls/${urlId}`
      const {code} = await this.$axios.$delete(url)
      _.set(res, 'code', code)
    } else if (param.items.length > 1) {
      const url = `${objApiPrefix}/webcategory/objects/${objectId}/urls`
      // 100개 이상을 제거할 경우 100개 단위로 쪼개어 요청
      const chunk = _.chunk(param.items, 100)
      const codeCnt = {ok: 0, pok: 0, nok: 0}
      for (let i = 0, l = _.size(chunk); i < l; i++) {
        let {code, result} = await this.$axios.$delete(url, {
          data: { items: chunk[i] }
        })
        codeCnt[code]++
        if (code !== 'ok') {
          res.errors = _.concat(res.errors, result.errors)
        }
      }
      if (codeCnt.ok > 0 && codeCnt.pok === 0 && codeCnt.nok === 0) res.code = 'ok'
      else if (codeCnt.ok === 0 && codeCnt.pok === 0 && codeCnt.nok > 0) res.code = 'nok'
      else res.code = 'pok'
    }
    if (res.code === 'ok' || res.code === 'pok') {
      dispatch('common/common/setIsChanged', true)
    }
    return res
  },
  // Url 추가
  async reqAddUrlData ({state, dispatch}, {loadingShow = true, loadingHide = true, successAlert = true} = {}) {
    const objectId = _.get(state.selectedData, 'web_cate_obj_id', void 0)
    const urlData = state.urlData
    const url = `${objApiPrefix}/webcategory/objects/${objectId}/urls`
    // TAMS (res)
    const res = await this.$axios.$post(url, urlData, {loadingShow, loadingHide, successAlert})
    if (res.code === 'ok') {
      this.dispatch('common/common/setIsChanged', true)
    }
    return res
  },
  async reqUpdateUrlData ({state, dispatch}, {loadingShow = true, loadingHide = true, successAlert = true} = {}) {
    const objectId = _.get(state.selectedData, 'web_cate_obj_id', void 0)
    const urlId = _.get(state.selectedUrlData, [0, 'web_cate_url_id'], void 0)
    const data = state.urlData
    const url = `${objApiPrefix}/webcategory/objects/${objectId}/urls/${urlId}`
    // TAMS (res)
    const res = await this.$axios.$put(url, data, {loadingShow, loadingHide, successAlert})
    if (res.code === 'ok') {
      this.dispatch('common/common/setIsChanged', true)
    }
    return res
  },
  // 웹 카테고리 객체 참조 여부 상세 조회
  async reqWebDetailRefData ({commit, state}, {objectData, loadingShow = true, loadingHide = true} = {}) {
    const objId = objectData.web_cate_obj_id
    const {code, result} = await this.$axios.$get(`${objApiPrefix}/webcategory/objects/${objId}/ref`, {loadingShow, loadingHide})
    if (code === 'ok') {
      commit('objectSecProfile/object/objectSettings/common/setUsedDetailData', result, {root: true})
    }
  }
}
